import { linkOptions, useNavigate, useParams, useSearch } from '@tanstack/react-router';

import { Breadcrumbs } from '../../../cdl/Breadcrumbs/Breadcrumbs';
import { Button } from '../../../cdl/Button/Button';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { Tabs } from '../../../cdl/Tabs/Tabs';
import { Text } from '../../../cdl/Text/Text';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { translate } from '../../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../../common/hooks/useDocumentTitle';
import { useRole } from '../../../common/hooks/useRole';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { useFlags } from '../../../common/launchDarkly/useFlags';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../../common/Page/Page';
import { Box } from '../../../common/ui/Box';
import { CustomerGroupTabs } from '../CustomerGroupTabs';

import { CreateCustomerCompany } from './components/CreateCustomerCompany';
import { CustomerDevelopersTab } from './components/CustomerDevelopersTab';
import { CustomerGroupDataSheet } from './components/CustomerGroupDataSheet';
import { CustomerHealthButton } from './components/CustomerHealthButton';
import { CustomersTable } from './components/CustomersTable';
import { EditCustomerGroupForm } from './components/EditCustomerGroupForm';
import { CustomerMfaSettings } from './components/MfaSettings';
import { ScheduleSharingSettings } from './components/ScheduleSharingSettings';
import { CustomerSsoSettings } from './components/sso/CustomerSsoSettings';
import { useCustomerGroupDetails } from './hooks/useCustomerGroupDetails';

export const CustomerGroupDetailPage = () => {
    const { groupId } = useParams({ from: '/_app/customer-groups/$groupId' });

    const role = useRole();
    const flags = useFlags();
    const { push, pop } = useDrawer();
    const navigate = useNavigate({ from: '/customer-groups/$groupId' });

    const customerGroupDetailsQuery = useCustomerGroupDetails(groupId);

    useDocumentTitle(translate('settings.groupManagement.title'));

    const { tab } = useSearch({ from: '/_app/customer-groups/$groupId/' });

    if (customerGroupDetailsQuery.isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (customerGroupDetailsQuery.isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const customerGroup = customerGroupDetailsQuery.data;

    const handleCustomerGroupEditClick = () => {
        const drawerId = push({
            header: { title: translate('customergroup.editTitle') },
            content: <EditCustomerGroupForm onDismiss={() => pop(drawerId)} initialValues={customerGroup} />,
        });
    };

    const handleCompanyCreateClick = () => {
        const drawerId = push({
            header: { title: translate('customer.createtitle') },
            content: (
                <CreateCustomerCompany
                    onSuccess={() => pop(drawerId)}
                    groupId={groupId}
                    isTestGroup={customerGroup.testCompany}
                />
            ),
        });
    };

    const breadcrumbLinks = role.isSuperAdmin()
        ? [{ label: translate('page.customers'), linkOptions: linkOptions({ to: '/customer-groups' }) }]
        : [{ label: translate('global.settings'), linkOptions: linkOptions({ to: '/settings' }) }];

    return (
        <Page>
            <Box display="flex" flexDirection="column" gap={4} marginBottom={16}>
                <Breadcrumbs links={breadcrumbLinks} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Text variant="headline" as="h1">
                        {translate('settings.groupManagement.title')}
                    </Text>

                    <Box display="flex" gap={8}>
                        {role.isSuperAdmin() ? (
                            <CustomerHealthButton customerGroupId={groupId} customerGroupName={customerGroup.name} />
                        ) : null}
                    </Box>
                </Box>

                <Text variant="body" color="foreground.subtle">
                    {customerGroup.name}
                </Text>
            </Box>

            <Tabs
                selectedContext={tab}
                onSelect={(newTab: CustomerGroupTabs) =>
                    navigate({ search: (previous) => ({ ...previous, tab: newTab }) })
                }
                totals={{
                    [CustomerGroupTabs.DETAILS]: null,
                    ...(flags.scheduleSharingSettings ? { [CustomerGroupTabs.SETTINGS]: null } : {}),
                    [CustomerGroupTabs.TWO_STEP_AUTH]: null,
                    [CustomerGroupTabs.SSO]: null,
                    ...(role.hasAdminRights() || role.isSuperAdmin() ? { [CustomerGroupTabs.DEVELOPERS]: null } : {}),
                }}
            />

            <Box marginTop={40}>
                {tab === CustomerGroupTabs.DETAILS ? (
                    <Box display="flex" flexDirection="column" gap={48}>
                        <Box display="flex" flexDirection="column" gap={16}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Text variant="subtitle1" as="h2">
                                    {translate('customers.detailsSectionHeadline')}
                                </Text>
                                {role.hasAdminRights() || role.isSuperAdmin() ? (
                                    <Button emphasis="medium" onClick={handleCustomerGroupEditClick}>
                                        {translate('actions.edit')}
                                    </Button>
                                ) : null}
                            </Box>
                            <CustomerGroupDataSheet customerGroup={customerGroup} />
                        </Box>

                        <Box display="flex" flexDirection="column" gap={16}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Text variant="subtitle1" as="h2">
                                    {translate('customers.subheadline')}
                                </Text>
                                {role.hasAdminRights() ? (
                                    <Button LeadingIcon={IconPlus} onClick={handleCompanyCreateClick}>
                                        {translate('customers.create')}
                                    </Button>
                                ) : null}
                            </Box>
                            <CustomersTable groupId={groupId} />
                        </Box>
                    </Box>
                ) : null}

                {tab === CustomerGroupTabs.TWO_STEP_AUTH ? <CustomerMfaSettings groupId={groupId} /> : null}

                {tab === CustomerGroupTabs.SSO ? <CustomerSsoSettings groupId={groupId} /> : null}

                {tab === CustomerGroupTabs.SETTINGS && flags.scheduleSharingSettings ? (
                    <ScheduleSharingSettings groupId={groupId} />
                ) : null}

                {tab === CustomerGroupTabs.DEVELOPERS ? <CustomerDevelopersTab groupId={groupId} /> : null}
            </Box>
        </Page>
    );
};
